import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes";
import Accordion from "../../components/Accordion";
import Button from "../../components/Button";
import { ButtonBackgroundColorType } from "../../components/Button/Button";
import { Container, HeighlightSpan, Paragraph, Question, WrapperSelect } from "./CircumstanceStep.style";
import { useStore } from "../../../../store/store";
import { useEffect, useMemo, useState } from "react";
import SelectVF from "../../components/SelectVF";
import ContactMeStep from "../ContactMeStep";
import Analytics from "../../../../utils/analytics";
import { TrackingFlowTypeEnum } from "../../../../types/TrackingFlowTypeEnum";
import InputVF from "../../components/InputVF";
import { InputTypeEnum } from "../../components/InputVF/InputVF";
import { UnemploymentInsuranceOptionModel } from "../../../../types/UnemploymentInsuranceOptionModel";

interface Props {
  step2: AccordionRoutesEnum;
}

const currentBenefitOptions: UnemploymentInsuranceOptionModel[] = [
  { id: "Barselsdagpenge", name: "Barselsdagpenge" },
  { id: "Efterloen", name: "Efterløn" },
  { id: "IngenYdelse", name: "Ingen ydelse" },
  { id: "KontanthjaelpEllerAndenOffentligYdelse", name: "Kontakt hjælp eller anden offentlig ydelse" },
  { id: "OenskerAtSoegeOmDagpenge", name: "Ønsker at søge om dagpenge" },
  { id: "Sygedagpenge", name: "Sygedagpenge" },
];

const salaryInsurancePaymentPeriodOptions: UnemploymentInsuranceOptionModel[] = [
  { id: "3", name: "3 måneder" },
  { id: "6", name: "6 måneder" },
  { id: "9", name: "9 måneder" },
];

export default function CircumstanceStep({step2}: Props) {
  const payload = useStore.usePayload();
  const setPayload = useStore.useSetPayload();
  const setAccordionRoute = useStore.useSetAccordionRoute();
  const setCompletedAccordionRoute = useStore.useSetCompletedAccordionRoute();
  const unimploymentInsuranceData = useStore.useUnimploymentInsuranceOptions();
  const unionData = useStore.useUnionOptions();
  const translations = useStore.useTranslations();
  const [showContactMe, setShowContactMe] = useState(false);
  const [error, setError] = useState(true);
  const [monthlySalaryInput, setMonthlySalaryInput] = useState<string>(payload.monthlySalary?.toString() ?? "");


  useMemo(() => {
    setPayload({haveYouEveryHadDanishUnemploymentInsurance: undefined})
  }, [])

  const unimploymentInsurenceOptions = () => {
    unimploymentInsuranceData.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    return unimploymentInsuranceData;
  };

  const unionOptions = () => {
    unionData.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    return unionData;
  };

  const canUnsubscribe = useMemo(() => {
    const union = unionData.find((o) => o.id === payload.currentUnion);

    if (union === null || union === undefined) {
      return true;
    } else if (union?.userUnsubscribe) {
      return true;
    } else {
      return false;
    }
  }, [payload.currentUnion, payload.currentlyMemberOfAUnion]);

  const wantsUnsubscribe = useMemo(() => {
    const union = unionData.find((o) => o.id === payload.currentUnion);

    if ((union !== null || union === undefined) && union?.userUnsubscribe) {
      return true;
    } else {
      return false;
    }
  }, [payload.currentUnion, payload.currentlyMemberOfAUnion]);

  useEffect(() => {
    if (!canUnsubscribe) {
      setPayload({ unsubscribeCurrentUnion: false });
    }
  }, [canUnsubscribe, payload.currentUnion, payload.currentlyMemberOfAUnion]);

  //hasUnemploymentInsuranceInAdvance (asked on OccupationStep) is the same question as currentlyHasUnemploymentBenefits, so keep them in sync
  //even if the user goes back and changes their answer there
  useEffect(() => {
    if (payload.hasUnemploymentInsuranceInAdvance === undefined || payload.hasUnemploymentInsuranceInAdvance === payload.currentlyHasUnemploymentBenefits) {
      return;
    }
    setPayload({
      currentlyHasUnemploymentBenefits: payload.hasUnemploymentInsuranceInAdvance,
      currentUnemploymentBenefits: undefined,
      haveYouRecentlyWorkedAbroad: undefined,
    });
  }, [payload.hasUnemploymentInsuranceInAdvance]);

  //Checking if there is a matching union for choosen unemploymentBenfits and setting union
  useEffect(() => {
    if (payload.currentUnemploymentBenefits) {
      const option = unimploymentInsurenceOptions().find((o) => o.id === payload.currentUnemploymentBenefits);
      const relatedUnion = option?.related;

      if (relatedUnion) {
        const unionOption = unionOptions().find((o) => o.id === relatedUnion);

        if (payload.wantsUnion) {
          setPayload({ currentUnion: unionOption?.id });
        } else {
          setPayload({ currentUnion: undefined });
        }
      }
    }
  }, [payload.currentUnemploymentBenefits, payload.wantsUnion]);
  //Checking matching union end

  //Validation CVR 
  const validationCVR = () => {
    if (payload.cvrNo !== undefined && !error) {
      return true;
    } else if (payload.cvrNo !== undefined && error) {
      return false;
    }
  };
  //Validation CVR end

  //Non-selfemployed with a-kasse need to answer if they have self-employed activity, and when the unemployment insurance should start
  const selfEmployedActivityAnswered = useMemo(() => {
    if (payload.occupation === "SelfEmployed" || !payload.wantsUnemploymentBenefits) {
      return true;
    }
    if (payload.haveSelfEmployedActivity === undefined) {
      return false;
    }
    if (!payload.unemploymentInsuranceStartDate) {
      return false;
    }
    return true;
  }, [payload.occupation, payload.wantsUnemploymentBenefits, payload.haveSelfEmployedActivity, payload.unemploymentInsuranceStartDate]);

  const unemploymentInsuranceStartDateLabel = useMemo(() => {
    return payload.currentlyHasUnemploymentBenefits
      ? "Hvornår skal din a-kasse overflyttes fra?"
      : "Hvornår skal din a-kasse starte?";
  }, [payload.currentlyHasUnemploymentBenefits]);

  const unionStartDateLabel = "Hvornår skal din nye fagforening starte?";

  //Unemployed users not currently receiving dagpenge need to answer which benefit they currently receive
  const currentBenefitReceivedAnswered = useMemo(() => {
    if (payload.occupation !== "Unemployed") {
      return true;
    }
    const notReceivingDagpenge = payload.currentlyHasUnemploymentBenefits === false || payload.areYouCurrentlyReceivingUnemploymentBenefits === false;
    if (!notReceivingDagpenge) {
      return true;
    }
    return payload.currentBenefitReceived !== undefined;
  }, [payload.occupation, payload.currentlyHasUnemploymentBenefits, payload.areYouCurrentlyReceivingUnemploymentBenefits, payload.currentBenefitReceived]);

  //Eligibility questions must all be answered before we know whether salary insurance is possible
  const salaryInsuranceEligibilityAnswered =
    payload.worksLessThan25HoursAWeek !== undefined &&
    payload.hasBeenUnemployedWithinLastTwoYears !== undefined &&
    payload.hasBeenNotifiedOfTermination !== undefined &&
    payload.hasTimeLimitedEmploymentContract !== undefined;

  //Users who chose salary insurance, alone or combined with union, must be eligible for it
  const salaryInsuranceNotEligible = (payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion) && (
    payload.worksLessThan25HoursAWeek === true ||
    payload.hasBeenUnemployedWithinLastTwoYears === true ||
    payload.hasBeenNotifiedOfTermination === true ||
    payload.hasTimeLimitedEmploymentContract === true
  );

  //Below this monthly salary, dagpenge alone covers the salary loss, so salary insurance provides no extra value
  const SALARY_INSURANCE_NOT_NEEDED_THRESHOLD = 29035;

  const salaryInsuranceNotNecessary = useMemo(() => {
    if (!payload.wantsSalaryInsurance && !payload.wantsSalaryInsuranceAndUnion) {
      return false;
    }
    if (!payload.monthlySalary) {
      return false;
    }
    return payload.monthlySalary < SALARY_INSURANCE_NOT_NEEDED_THRESHOLD;
  }, [payload.wantsSalaryInsurance, payload.wantsSalaryInsuranceAndUnion, payload.monthlySalary]);

  //Removes only the salary insurance part of the choosen product, keeping a standalone union membership if it was part of a combined product
  const removeSalaryInsurance = () => {
    if (payload.wantsSalaryInsuranceAndUnion) {
      setPayload({
        wantsSalaryInsuranceAndUnion: false,
        wantsUnion: true,
        currentlyHasSalaryInsurance: undefined,
        salaryInsurancePaymentPeriod: undefined,
        salaryInsuranceBenefitAmount: undefined,
      });
    } else {
      setPayload({
        wantsSalaryInsurance: false,
        currentlyHasSalaryInsurance: undefined,
        salaryInsurancePaymentPeriod: undefined,
        salaryInsuranceBenefitAmount: undefined,
      });
    }
  };

  //Ydelse options use the same max-allowed-insurance formula as the salary insurance calculator, capped at 30.000 kr. in steps of 1.000 kr.
  const salaryInsuranceBenefitAmountOptions = (): UnemploymentInsuranceOptionModel[] => {
    const DAILY_ALLOWANCE = 22041;
    const salary = monthlySalaryInput ? Number(monthlySalaryInput) : 0;
    const adjustedMaxPayout = Math.round(salary * 0.9 * (1 - 0.08));
    const maxAllowedInsurance = Math.max(0, adjustedMaxPayout - DAILY_ALLOWANCE);
    const maxBenefit = Math.floor(Math.min(maxAllowedInsurance, 30000) / 1000) * 1000;
    const options: UnemploymentInsuranceOptionModel[] = [];
    for (let amount = 2000; amount <= maxBenefit; amount += 1000) {
      options.push({ id: amount.toString(), name: `${amount} kr.` });
    }
    return options;
  };

  //Users who chose salary insurance, alone or combined with union, need to answer if they already have salary insurance and answer the eligibility questions
  const salaryInsuranceAndUnionAnswered = useMemo(() => {
    if (!payload.wantsSalaryInsurance && !payload.wantsSalaryInsuranceAndUnion) {
      return true;
    }
    if (payload.currentlyHasSalaryInsurance === undefined) {
      return false;
    }
    if (!salaryInsuranceEligibilityAnswered) {
      return false;
    }
    if (salaryInsuranceNotEligible) {
      return false;
    }
    //Monthly salary is needed for both products, but payment period and benefit amount only apply to standalone salary insurance
    if (!payload.monthlySalary) {
      return false;
    }
    if (payload.wantsSalaryInsurance && (payload.salaryInsurancePaymentPeriod === undefined || payload.salaryInsuranceBenefitAmount === undefined)) {
      return false;
    }
    return true;
  }, [payload.wantsSalaryInsurance, payload.wantsSalaryInsuranceAndUnion, payload.currentlyHasSalaryInsurance, salaryInsuranceEligibilityAnswered, salaryInsuranceNotEligible, payload.monthlySalary, payload.salaryInsurancePaymentPeriod, payload.salaryInsuranceBenefitAmount]);

  //Users who want a union product need to fill in when the new union should start
  const unionStartDateAnswered = useMemo(() => {
    if (!payload.wantsUnion && !payload.wantsSalaryInsuranceAndUnion) {
      return true;
    }
    return !!payload.unionStartDate;
  }, [payload.wantsUnion, payload.wantsSalaryInsuranceAndUnion, payload.unionStartDate]);

  //Track questions showed
  const setTracking = (question: string) => {
    // Analytics.getInstance().trackCircumstanceStep(question, "circumstance_questions");
  }
  //End tracking

  //Unemployed users not already in an a-kasse can't join one until they're employed again
  const unemployedCannotJoinAkasse = payload.occupation === "Unemployed" && payload.currentlyHasUnemploymentBenefits === false;

  //Unemployed users already in an a-kasse but not currently receiving dagpenge can't be transferred right away
  const unemployedNotReceivingBenefits = payload.occupation === "Unemployed" && payload.currentlyHasUnemploymentBenefits === true && payload.areYouCurrentlyReceivingUnemploymentBenefits === false;

  const nextStepAllowed = useMemo(() => {
    if (unemployedCannotJoinAkasse || unemployedNotReceivingBenefits) {
      return false;
    }
    if (!selfEmployedActivityAnswered || !currentBenefitReceivedAnswered || !salaryInsuranceAndUnionAnswered || !unionStartDateAnswered) {
      return false;
    }
    //If only the product "a-kasse" is choosen
    if (payload.wantsUnemploymentBenefits && !payload.wantsUnion && !payload.wantsBasic && !payload.wantsHRJura) {
        if (payload.currentlyHasUnemploymentBenefits === undefined) {
          return false;
        }
        if ((payload.currentlyHasUnemploymentBenefits === undefined || payload.currentlyHasUnemploymentBenefits === false) && payload.haveYouRecentlyWorkedAbroad === undefined) {
          return false;
        }
        if (payload.currentlyHasUnemploymentBenefits && payload.currentUnemploymentBenefits !== undefined) {
          return true;
        }
        if ((!payload.currentlyHasUnemploymentBenefits && payload.haveYouEveryHadDanishUnemploymentInsurance !== undefined) && ((!payload.haveYouEveryHadDanishUnemploymentInsurance) || (payload.haveYouEveryHadDanishUnemploymentInsurance && payload.currentUnemploymentBenefits !== undefined))) { 
          return true
        }
        if (!payload.currentlyHasUnemploymentBenefits && payload.haveYouEveryHadDanishUnemploymentInsurance && payload.currentUnemploymentBenefits !== undefined) {
          return true
        }

        return false;
      //If the product "HR-Jura" or "Basis" is choosen  
    } else if (payload.wantsBasic || payload.wantsHRJura) {
        if (payload.currentlyHasUnemploymentBenefits === undefined) {
          return false;
        }
        if (payload.currentlyHasUnemploymentBenefits && payload.currentUnemploymentBenefits !== undefined && (payload.doYouRunYourOwnBusiness !== undefined && (payload.doYouRunYourOwnBusiness && payload.cvrNo !== undefined || payload.doYouRunYourOwnBusiness === false))) {
          return true;
        }
        if ((payload.doYouRunYourOwnBusiness !== undefined && (payload.doYouRunYourOwnBusiness && payload.cvrNo !== undefined || payload.doYouRunYourOwnBusiness === false)) && (!payload.currentlyHasUnemploymentBenefits && payload.haveYouEveryHadDanishUnemploymentInsurance !== undefined && ((!payload.haveYouEveryHadDanishUnemploymentInsurance) || (payload.haveYouEveryHadDanishUnemploymentInsurance && payload.currentUnemploymentBenefits !== undefined)))) { 
          return true
        }
        if ((payload.doYouRunYourOwnBusiness !== undefined && (payload.doYouRunYourOwnBusiness && payload.cvrNo !== undefined || payload.doYouRunYourOwnBusiness === false)) && (!payload.currentlyHasUnemploymentBenefits && payload.haveYouEveryHadDanishUnemploymentInsurance && payload.currentUnemploymentBenefits !== undefined)) {
          return true
        }

        return false;
      //if only the product "union" is choosen
    } else if ((payload.wantsUnion && !payload.wantsUnemploymentBenefits)) {
        if (payload.currentlyMemberOfAUnion === undefined) {
          return false;
        }
        if (!payload.currentlyMemberOfAUnion || (payload.currentlyMemberOfAUnion && payload.currentUnion !== undefined)) {
          return true;
        }
        return false;
      //If both products are choosen
    } else if ((payload.wantsUnemploymentBenefits && payload.wantsUnion)) {
        if (payload.currentlyHasUnemploymentBenefits === undefined || payload.currentlyMemberOfAUnion === undefined) {
          return false;
        }
        if ((payload.currentlyHasUnemploymentBenefits === undefined || payload.currentlyHasUnemploymentBenefits === false) && payload.haveYouRecentlyWorkedAbroad === undefined) {
          return false;
        }
        if (!payload.currentlyHasUnemploymentBenefits && !payload.currentlyMemberOfAUnion) {
          return true;
        }
        if (payload.currentlyHasUnemploymentBenefits && payload.currentUnemploymentBenefits !== undefined && payload.currentlyMemberOfAUnion === undefined) {
          return false
        }
        if ((!payload.currentlyHasUnemploymentBenefits || (payload.currentlyHasUnemploymentBenefits && payload.currentUnemploymentBenefits !== undefined)) && (!payload.currentlyMemberOfAUnion || (payload.currentlyMemberOfAUnion && payload.currentUnion !== undefined))) {
          return true;
        }
        return false;
        //Fallback false
    } else {
      return false;
    }
  }, [payload, selfEmployedActivityAnswered, currentBenefitReceivedAnswered, salaryInsuranceAndUnionAnswered, unionStartDateAnswered]);


  const next = () => {
    if (!payload.isDanishResident) {
        setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
        setPayload({ cause: "Bor i udlandet" });
        setShowContactMe(true);
    } else if (payload.occupation === "Unemployed" && payload.wantsUnemploymentBenefits && payload.currentlyHasUnemploymentBenefits !== undefined) {
      setAccordionRoute(AccordionRoutesEnum.InformationStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
    } else if(payload.occupation === "SelfEmployed" && payload.doYouRunYourOwnBusiness === false) {
        setPayload({ cause: "Selvstændig men ikke på fuldtid" });
        setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
        setShowContactMe(true); 
    } else if ((payload.occupation !== "Unemployed" || (payload.wantsUnion && !payload.wantsUnemploymentBenefits)) && payload.areYouPrimarilyWorkingInDenmark) {
      setAccordionRoute(AccordionRoutesEnum.InformationStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
    } else if (!payload.areYouPrimarilyWorkingInDenmark) {
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
      setPayload({ cause: "Bor i udlandet" });
      setShowContactMe(true);
    } 
    // if you recent worked abroad and wants unemployment benefits
    if (payload.haveYouRecentlyWorkedAbroad && payload.wantsUnemploymentBenefits && !payload.currentlyHasUnemploymentBenefits) {
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
      setPayload({ cause: "Har for nyligt arbejdet i udlandet" });
      setShowContactMe(true);
    } 
  };

  const closeModal = () => {
    setAccordionRoute(AccordionRoutesEnum.CircumstanceStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
    //setPayload({ cause: "" });
    setShowContactMe(false);
  }

  const openContactMeStep = (cause: string) => {
    if(cause === "notDanishResident") {
      setPayload({ isDanishResident: false });
      setPayload({ cause: "Bor i udlandet" });
      setShowContactMe(true);
    }
    else if(cause === "notPrimarilyWorkingInDenmark") {
      setPayload({ areYouPrimarilyWorkingInDenmark: false })
      setPayload({ cause: "Arbejder ikke primært i Danmark" });
      setShowContactMe(true);
    }
  }

  return (
    <>
      <Accordion
        title="3. Arbejde og medlemskab"
        step={AccordionRoutesEnum.CircumstanceStep}
        onNext={() => {
          // Add tracking
          if (payload.currentlyHasUnemploymentBenefits) {
            // Analytics.getInstance().trackTerminateUnemploymentBenefits(step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
          }
          if (payload.unsubscribeCurrentUnion) {
            // Analytics.getInstance().trackTerminateUnion(step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
          }
          next();
          setCompletedAccordionRoute(AccordionRoutesEnum.CircumstanceStep);
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }}
        stepComplete={nextStepAllowed}
        previousStep={step2}
        headerImg="/assets/images/ar-on-you-outline.svg"
        step2={step2}
      >
        <Question>{translations?.circumstanceStep.title}</Question>
        <Container>
          <Button onClick={() => setPayload({ isDanishResident: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.isDanishResident === true}>
            {translations?.general.answerYes}
          </Button>
          <Button onClick={() => openContactMeStep("notDanishResident")} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.isDanishResident === false}>
          {translations?.general.answerNo}
          </Button>
        </Container>
        <Question>{translations?.workingDenmarkStep.title}</Question>
        <Container>
          <Button onClick={() => setPayload({ areYouPrimarilyWorkingInDenmark: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.areYouPrimarilyWorkingInDenmark === true}>
            {translations?.general.answerYes}
          </Button>
          <Button onClick={() => openContactMeStep("notPrimarilyWorkingInDenmark")} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.areYouPrimarilyWorkingInDenmark === false}>
            {translations?.general.answerNo}
          </Button>
        </Container>
        {(payload.occupation === "SelfEmployed") && (
          <>
            <Question>{translations?.circumstanceStep.questionSelfemployed}</Question>
            <Container>
              <Button 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={true} 
                isChecked={payload.doYouRunYourOwnBusiness && payload.doYouRunYourOwnBusiness !== undefined} 
                onClick={() => {
                  setPayload({ doYouRunYourOwnBusiness: true })
                  setTracking("Indtast dit CVR-nummer")
                }}
              >
                {translations?.general.answerYes}
              </Button>
              <Button 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={true} 
                isChecked={!payload.doYouRunYourOwnBusiness && payload.doYouRunYourOwnBusiness !== undefined} 
                onClick={() => setPayload({ doYouRunYourOwnBusiness: false, cvrNo: undefined })}
              >
                {translations?.general.answerNo}
              </Button> 
            </Container>
          </>
        )}
        {payload.doYouRunYourOwnBusiness && (
          <>
            <WrapperSelect>
              <InputVF
                id="CVR" 
                name="CVRinput" 
                type={InputTypeEnum.text} 
                labelText="CVR-nummer" 
                placeholder="Indtast dit CVR-nummer"
                autocomplete="on" 
                onChange={(e) => {
                  setPayload({ cvrNo: e.currentTarget.value });
                  const reg = /[0-9]{8}/
                  if (!reg.test(e.currentTarget.value)) {
                    setError(true);
                  } else {
                    setError(false);
                  }
                }}
                maxlength={8}
                validated={validationCVR()} 
                step={step2} 
              />
            </WrapperSelect>
          </>  
        )}
        {payload.wantsUnemploymentBenefits && (
          <>
            {payload.hasUnemploymentInsuranceInAdvance === undefined && (
              <>
                <Question>{translations?.unemploymentInsuranceStep.title}</Question>
                <Container>
                  <Button 
                    onClick={() => {
                      setPayload({ currentlyHasUnemploymentBenefits: true, haveYouRecentlyWorkedAbroad: undefined })
                      setTracking("Hvilken nuværende akasse")
                      if(payload.occupation === "Unemployed") {
                        setTracking("Modtager du dagpenge") 
                      }
                    }} 
                    bgColor={ButtonBackgroundColorType.Blue} 
                    blockButton={false} 
                    isChecked={payload.currentlyHasUnemploymentBenefits === true}
                  >
                      {translations?.general.answerYes}
                  </Button>
                  <Button 
                    onClick={() => {
                      setPayload({ currentlyHasUnemploymentBenefits: false, currentUnemploymentBenefits: undefined})
                      setTracking("Har du tidligere været medlem af en a-kasse")
                    }} 
                    bgColor={ButtonBackgroundColorType.Blue} 
                    blockButton={false} 
                    isChecked={payload.currentlyHasUnemploymentBenefits === false}
                  >
                      {translations?.general.answerNo}
                  </Button>
                </Container>
                {unemployedCannotJoinAkasse && (
                  <Paragraph>
                    <HeighlightSpan>OBS!</HeighlightSpan> Du kan først indmeldelses, når du er i job
                  </Paragraph>
                )}
              </>
            )}
            {payload.currentlyHasUnemploymentBenefits && (
              <WrapperSelect>
                <SelectVF
                  id="chooseCurrentUnemploymentInscurance"
                  name="unemploymentInscurance"
                  options={unimploymentInsurenceOptions()}
                  placeholder="Vælg nuværende a-kasse"
                  label="Hvilken a-kasse?"
                  selectedOption={payload.currentUnemploymentBenefits}
                  step={AccordionRoutesEnum.CircumstanceStep}
                  onSelect={(id) => {
                    setPayload({ currentUnemploymentBenefits: id });
                  }}
                />
                <Paragraph>{translations?.unemploymentInsuranceStep.description}</Paragraph>
              </WrapperSelect>
            )}
            {payload.occupation !== "SelfEmployed" && (
              <WrapperSelect>
                <InputVF
                  id="unemploymentInsuranceStartDate"
                  name="unemploymentInsuranceStartDate"
                  type={InputTypeEnum.date}
                  labelText={unemploymentInsuranceStartDateLabel}
                  placeholder="Vælg dato"
                  autocomplete="off"
                  value={payload.unemploymentInsuranceStartDate}
                  validated={undefined}
                  min={new Date().toISOString().split("T")[0]}
                  step={AccordionRoutesEnum.CircumstanceStep}
                  onChange={(e) => setPayload({ unemploymentInsuranceStartDate: e.currentTarget.value })}
                />
              </WrapperSelect>
            )}
            {payload.occupation === "Unemployed" && payload.currentlyHasUnemploymentBenefits === false && (
              <WrapperSelect>
                <SelectVF
                  id="currentBenefitReceived"
                  name="currentBenefitReceived"
                  options={currentBenefitOptions}
                  placeholder="Vælg ydelse"
                  label="Hvilken ydelse får du i dag?"
                  selectedOption={payload.currentBenefitReceived}
                  step={AccordionRoutesEnum.CircumstanceStep}
                  onSelect={(id) => {
                    setPayload({ currentBenefitReceived: id });
                  }}
                />
              </WrapperSelect>
            )}
            {!payload.currentlyHasUnemploymentBenefits && payload.wantsUnemploymentBenefits && payload.currentlyHasUnemploymentBenefits !== undefined && (
              <>
              <Question>{translations?.circumstanceStep.questionWorkAbroad}</Question>
              <Container>
                      <Button onClick={() => setPayload({ haveYouRecentlyWorkedAbroad: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.haveYouRecentlyWorkedAbroad === true}>
                        {translations?.general.answerYes}
                      </Button>
                      <Button onClick={() => setPayload({ haveYouRecentlyWorkedAbroad: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.haveYouRecentlyWorkedAbroad === false}>
                        {translations?.general.answerNo}
                      </Button>
                    </Container>
              </>
            )}
            {(payload.occupation === "Unemployed" && payload.currentlyHasUnemploymentBenefits) && (
              <>
                <Question>{translations?.receivingUnemploymentBenefitsStep.title}</Question>
                <Container>
                  <Button onClick={() => setPayload({ areYouCurrentlyReceivingUnemploymentBenefits: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.areYouCurrentlyReceivingUnemploymentBenefits === true}>
                    {translations?.general.answerYes}
                  </Button>
                  <Button onClick={() => setPayload({ areYouCurrentlyReceivingUnemploymentBenefits: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.areYouCurrentlyReceivingUnemploymentBenefits === false}>
                    {translations?.general.answerNo}
                  </Button>
                </Container>
                {payload.areYouCurrentlyReceivingUnemploymentBenefits === false && (
                  <Paragraph>
                    <HeighlightSpan>OBS!</HeighlightSpan> Medlem kan tidligst overflyttes løbende + en måned
                  </Paragraph>
                )}
              </>
            )}
            {payload.occupation === "Unemployed" && payload.currentlyHasUnemploymentBenefits && payload.areYouCurrentlyReceivingUnemploymentBenefits === false && (
              <WrapperSelect>
                <SelectVF
                  id="currentBenefitReceived"
                  name="currentBenefitReceived"
                  options={currentBenefitOptions}
                  placeholder="Vælg ydelse"
                  label="Hvilken ydelse får du i dag?"
                  selectedOption={payload.currentBenefitReceived}
                  step={AccordionRoutesEnum.CircumstanceStep}
                  onSelect={(id) => {
                    setPayload({ currentBenefitReceived: id });
                  }}
                />
              </WrapperSelect>
            )}
            {(!payload.currentlyHasUnemploymentBenefits && payload.currentlyHasUnemploymentBenefits !== undefined) && (
              <>
               <Question>{translations?.fitWithYouStep.priorHadUnemploymentInsurance}</Question>
               <Container>
                 <Button 
                    onClick={() => {
                      setPayload({ haveYouEveryHadDanishUnemploymentInsurance: true })
                      setTracking("Hvilken tidligere akasse")
                    }} 
                    bgColor={ButtonBackgroundColorType.Blue} 
                    blockButton={false} 
                    isChecked={payload.haveYouEveryHadDanishUnemploymentInsurance === true}
                  >
                   {translations?.general.answerYes}
                 </Button>
                 <Button 
                    onClick={() => {
                      setPayload({ haveYouEveryHadDanishUnemploymentInsurance: false,  currentUnemploymentBenefits: undefined  })
                    }} 
                    bgColor={ButtonBackgroundColorType.Blue} 
                    blockButton={false} 
                    isChecked={payload.haveYouEveryHadDanishUnemploymentInsurance === false}
                  >
                   {translations?.general.answerNo}
                 </Button>
               </Container>
              </>
            )}
            {(payload.haveYouEveryHadDanishUnemploymentInsurance && !payload.currentlyHasUnemploymentBenefits) && (
              <WrapperSelect>
                <SelectVF
                  id="choosePreviousUnemploymentInscurance"
                  name="previousUnemploymentInscurance"
                  options={unimploymentInsurenceOptions()}
                  placeholder="Vælg tidligere a-kasse"
                  label="Hvilken a-kasse?"
                  selectedOption={payload.currentUnemploymentBenefits}
                  step={AccordionRoutesEnum.CircumstanceStep}
                  onSelect={(id) => {
                    setPayload({ currentUnemploymentBenefits: id, haveYouEveryHadDanishUnemploymentInsurance: true});
                  }}
                />
              </WrapperSelect>
            )}
            {payload.occupation !== "SelfEmployed" && (
              <>
                <Question>Har du selvstændig aktivitet?</Question>
                <Container>
                  <Button
                    onClick={() => setPayload({ haveSelfEmployedActivity: true })}
                    bgColor={ButtonBackgroundColorType.Blue}
                    blockButton={false}
                    isChecked={payload.haveSelfEmployedActivity === true}
                  >
                    {translations?.general.answerYes}
                  </Button>
                  <Button
                    onClick={() => setPayload({ haveSelfEmployedActivity: false })}
                    bgColor={ButtonBackgroundColorType.Blue}
                    blockButton={false}
                    isChecked={payload.haveSelfEmployedActivity === false}
                  >
                    {translations?.general.answerNo}
                  </Button>
                </Container>
              </>
            )}
          </>
        )}
        {(payload.wantsUnion || payload.wantsSalaryInsuranceAndUnion) && (
          <>
            <Question>{translations?.unionStep.title}</Question>
            <Container>
              <Button onClick={() => {
                  setPayload({ currentlyMemberOfAUnion: true })
                  setTracking("Hvilken fagforening")
                }} 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={false} 
                isChecked={payload.currentlyMemberOfAUnion === true}
              >
                {translations?.general.answerYes}
              </Button>
              <Button onClick={() => setPayload({ currentlyMemberOfAUnion: false,  currentUnion: undefined})} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.currentlyMemberOfAUnion === false}>
                {translations?.general.answerNo}
              </Button>
            </Container>
            <WrapperSelect>
              <InputVF
                id="unionStartDate"
                name="unionStartDate"
                type={InputTypeEnum.date}
                labelText={unionStartDateLabel}
                placeholder="Vælg dato"
                autocomplete="off"
                value={payload.unionStartDate}
                validated={payload.unionStartDate ? true : undefined}
                min={new Date().toISOString().split("T")[0]}
                step={AccordionRoutesEnum.CircumstanceStep}
                onChange={(e) => setPayload({ unionStartDate: e.currentTarget.value })}
              />
            </WrapperSelect>
            {payload.currentlyMemberOfAUnion && (
              <>
                <WrapperSelect>
                  <SelectVF
                    id="chooseCurrentUnion"
                    name="union"
                    options={unionOptions()}
                    defaultOption={payload.currentUnion === undefined ? "Vælg fagforening" : unionOptions().find((p) => p.id === payload.currentUnion)?.name}
                    label="Hvilken fagforening?"
                    selectedOption={payload.currentUnion}
                    step={AccordionRoutesEnum.CircumstanceStep}
                    onSelect={(id) => {
                      setPayload({ currentUnion: id });
                    }}
                  />
                  {!canUnsubscribe && (
                    <Paragraph>
                      <HeighlightSpan>OBS!</HeighlightSpan> Vi kan desværre ikke flytte dig automatisk, da din fagforening ikke tillader det. Du skal derfor selv sørge for at opsige din nuværende fagforening.
                    </Paragraph>
                  )}
                </WrapperSelect>
                {wantsUnsubscribe && (
                  <>
                    <Question>{translations?.circumstanceStep.questionUnsubscribeUnion}</Question>
                    <Container>
                      <Button onClick={() => setPayload({ unsubscribeCurrentUnion: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.unsubscribeCurrentUnion === true}>
                        {translations?.general.answerYes}
                      </Button>
                      <Button onClick={() => setPayload({ unsubscribeCurrentUnion: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.unsubscribeCurrentUnion === false}>
                        {translations?.general.answerNo}
                      </Button>
                    </Container>
                  </>
                )}
              </>
            )}
          </>
        )}
        {(payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion) && (
          <>
            <Question>Har en lønsikring i dag?</Question>
            <Container>
              <Button onClick={() => setPayload({ currentlyHasSalaryInsurance: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.currentlyHasSalaryInsurance === true}>
                {translations?.general.answerYes}
              </Button>
              <Button onClick={() => setPayload({ currentlyHasSalaryInsurance: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.currentlyHasSalaryInsurance === false}>
                {translations?.general.answerNo}
              </Button>
            </Container>
            <Question>Arbejder du mindre end 25 timer om ugen på samme job?</Question>
            <Container>
              <Button onClick={() => setPayload({ worksLessThan25HoursAWeek: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.worksLessThan25HoursAWeek === true}>
                {translations?.general.answerYes}
              </Button>
              <Button onClick={() => setPayload({ worksLessThan25HoursAWeek: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.worksLessThan25HoursAWeek === false}>
                {translations?.general.answerNo}
              </Button>
            </Container>
            <Question>Har du været ledig inden for de sidste 2 år?</Question>
            <Container>
              <Button onClick={() => setPayload({ hasBeenUnemployedWithinLastTwoYears: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.hasBeenUnemployedWithinLastTwoYears === true}>
                {translations?.general.answerYes}
              </Button>
              <Button onClick={() => setPayload({ hasBeenUnemployedWithinLastTwoYears: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.hasBeenUnemployedWithinLastTwoYears === false}>
                {translations?.general.answerNo}
              </Button>
            </Container>
            <Question>Er du blevet varslet opsagt?</Question>
            <Container>
              <Button onClick={() => setPayload({ hasBeenNotifiedOfTermination: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.hasBeenNotifiedOfTermination === true}>
                {translations?.general.answerYes}
              </Button>
              <Button onClick={() => setPayload({ hasBeenNotifiedOfTermination: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.hasBeenNotifiedOfTermination === false}>
                {translations?.general.answerNo}
              </Button>
            </Container>
            <Question>Er din ansættelseskontrakt tidsbegrænset (har en slutdato)?</Question>
            <Container>
              <Button onClick={() => setPayload({ hasTimeLimitedEmploymentContract: true })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.hasTimeLimitedEmploymentContract === true}>
                {translations?.general.answerYes}
              </Button>
              <Button onClick={() => setPayload({ hasTimeLimitedEmploymentContract: false })} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={payload.hasTimeLimitedEmploymentContract === false}>
                {translations?.general.answerNo}
              </Button>
            </Container>
            {salaryInsuranceNotEligible && (
              <Paragraph>
                <HeighlightSpan>OBS!</HeighlightSpan> Lønsikring er desværre ikke en mulighed for dig lige nu.
              </Paragraph>
            )}
            {(payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion) && salaryInsuranceEligibilityAnswered && !salaryInsuranceNotEligible && (
              <>
                <WrapperSelect>
                  <InputVF
                    id="monthlySalary"
                    name="monthlySalary"
                    type={InputTypeEnum.number}
                    labelText="Månedsløn"
                    placeholder="Indtast din månedsløn"
                    autocomplete="off"
                    value={monthlySalaryInput}
                    validated={payload.monthlySalary ? true : undefined}
                    step={AccordionRoutesEnum.CircumstanceStep}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setMonthlySalaryInput(value);
                      const salary = value ? Number(value) : undefined;
                      setPayload({ monthlySalary: salary, salaryInsuranceBenefitAmount: undefined });
                    }}
                  />
                </WrapperSelect>
                {salaryInsuranceNotNecessary && (
                  <>
                    <Paragraph>
                      <HeighlightSpan>OBS!</HeighlightSpan> Lønsikring er ikke nødvendigt - Dagpenge dækket behovet
                    </Paragraph>
                    <Container>
                      <Button onClick={removeSalaryInsurance} bgColor={ButtonBackgroundColorType.Blue} blockButton={false} isChecked={false}>
                        Fjern lønsikring
                      </Button>
                    </Container>
                  </>
                )}
              </>
            )}
            {payload.wantsSalaryInsurance && salaryInsuranceEligibilityAnswered && !salaryInsuranceNotEligible && !salaryInsuranceNotNecessary && (
              <>
                <WrapperSelect>
                  <SelectVF
                    id="salaryInsurancePaymentPeriod"
                    name="salaryInsurancePaymentPeriod"
                    options={salaryInsurancePaymentPeriodOptions}
                    placeholder="Vælg udbetalingsperiode"
                    label="Udbetalingsperiode"
                    selectedOption={payload.salaryInsurancePaymentPeriod?.toString()}
                    step={AccordionRoutesEnum.CircumstanceStep}
                    onSelect={(id) => setPayload({ salaryInsurancePaymentPeriod: Number(id) })}
                  />
                </WrapperSelect>
                <WrapperSelect>
                  <SelectVF
                    id="salaryInsuranceBenefitAmount"
                    name="salaryInsuranceBenefitAmount"
                    options={salaryInsuranceBenefitAmountOptions()}
                    placeholder="Vælg ydelse"
                    label="Ydelse"
                    selectedOption={payload.salaryInsuranceBenefitAmount?.toString()}
                    step={AccordionRoutesEnum.CircumstanceStep}
                    onSelect={(id) => setPayload({ salaryInsuranceBenefitAmount: Number(id) })}
                  />
                </WrapperSelect>
              </>
            )}
          </>
        )}
      </Accordion>

      <ContactMeStep showContactMe={showContactMe} onClose={closeModal} />
    </>
  );
}
